import { mapRecordToArray } from '@deity-io/di-data-basics';
import type { IRestEndpointConfig } from '@deity-io/di-data-flows';
import { mergeEndpointAndService } from '@deity-io/di-data-flows-editor';
import RequestExplorerProvider from 'contexts/RequestExplorer/RequestExplorerProvider';
import { UIContext } from 'contexts/UIContext';
import { CACHE_FIRST, ENTITY_PATH, ENTITY_PATH_ID } from 'contracts/constants';
import type { FC } from 'react';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetPackageVersionQuery } from 'services/graphql/generated/hooks';
import { getParams } from 'utils';
import { buildConfigurationStore } from 'utils/build-configuration-store';
import RequestExplorer from './RequestExplorer';

interface RequestExplorerProps {
  locked?: boolean;
  endpoint?: Partial<IRestEndpointConfig>;
  service?: Partial<IRestEndpointConfig>;
  packageVersionUuid: string;
}
/**
 * Provides the context, now bound to the Endpoint (context)
 */
const RequestExplorerWrapper: FC<RequestExplorerProps> = ({
  endpoint: endpointInput,
  service: serviceInput,
  packageVersionUuid
}) => {
  const ctxUI = useContext(UIContext);

  /**
   * Todo, get this info from the endpoint
   */
  const { pathname } = useLocation();
  const urlParams = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);

  const endpoint = mergeEndpointAndService(endpointInput, serviceInput);

  const params = endpoint?.variables
    ? mapRecordToArray(endpoint?.variables, (value, key) => [key, value] as [string, string])
    : [];
  const headers = endpoint?.headers
    ? mapRecordToArray(endpoint?.headers, (value, key) => [key, value] as [string, string])
    : [];
  let url = `${endpoint?.protocol}://${endpoint?.host}${endpoint?.path}`;
  if (params.length) {
    url += '?';
    url += params.map(param => `${param[0]}=${param[1]}`).join('&');
  }
  const { data } = useGetPackageVersionQuery({
    variables: {
      uuid: urlParams?.versionUuid
    },
    skip: !urlParams?.versionUuid,
    ...CACHE_FIRST
  });
  if (!data) {
    return null;
  }

  const availableVariableValues = buildConfigurationStore(ctxUI?.state.currentEnvironment, data);

  return (
    <RequestExplorerProvider
      method={endpoint?.method}
      url={url}
      headers={headers}
      structureUuid={typeof endpoint?.responseStructure === 'string' ? endpoint?.responseStructure : undefined}
      packageVersionUuid={packageVersionUuid}
      authentication={endpoint?.authentication}
    >
      <RequestExplorer availableVariableValues={availableVariableValues} />
    </RequestExplorerProvider>
  );
};

export default RequestExplorerWrapper;
